import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Shield, AlertTriangle, Scale, ArrowLeft } from 'lucide-react';
import { Button } from '@components/common/Button';

export const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-ncaa-primary hover:text-blue-800 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-ncaa-primary text-white rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Terms and Conditions</h1>
                <p className="text-gray-600 mt-1">
                  DCEV Portal - Digital Civil Aviation Enrollment
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Effective Date:</strong> January 1, 2024
                <br />
                <strong>Last Updated:</strong> September 29, 2024
                <br />
                <strong>Version:</strong> 1.0
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <div className="flex items-center space-x-2 mb-4">
                <Scale className="h-6 w-6 text-ncaa-primary" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">1. Introduction</h2>
              </div>
              <p className="text-gray-700 leading-relaxed">
                These Terms and Conditions ("Terms") govern your use of the Digital Civil Aviation
                Verification (DCEV) Portal operated by the Nigeria Civil Aviation Authority (NCAA).
                By creating an account and using this service, you agree to be bound by these Terms
                and all applicable laws and regulations.
              </p>
            </section>

            {/* Purpose */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Purpose of Service</h2>
              <p className="text-gray-700 mb-3 leading-relaxed">
                The DCEV Portal is designed to facilitate:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Aviation license applications and renewals</li>
                <li>Document submission and verification</li>
                <li>Appointment scheduling at testing centers</li>
                <li>Application status tracking</li>
                <li>Communication between applicants and NCAA officials</li>
              </ul>
            </section>

            {/* Eligibility */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Eligibility</h2>
              <p className="text-gray-700 mb-3 leading-relaxed">To use this service, you must:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Be at least 18 years of age</li>
                <li>Hold a valid Nigerian National Identification Number (NIN)</li>
                <li>Provide accurate and truthful information</li>
                <li>Have the legal capacity to enter into this agreement</li>
              </ul>
            </section>

            {/* Data Collection */}
            <section className="mb-8">
              <div className="flex items-center space-x-2 mb-4">
                <Shield className="h-6 w-6 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">
                  4. Data Collection and Privacy
                </h2>
              </div>
              <p className="text-gray-700 mb-3 leading-relaxed">
                We collect and process your personal information in accordance with:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Nigeria Data Protection Regulation (NDPR) 2019</li>
                <li>NCAA data handling policies</li>
                <li>International civil aviation security standards</li>
              </ul>
              <p className="text-gray-700 mt-4 leading-relaxed">
                Your data will be used solely for aviation licensing purposes and may be shared with
                relevant authorities as required by law.
              </p>
            </section>

            {/* User Responsibilities */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. User Responsibilities</h2>
              <p className="text-gray-700 mb-3 leading-relaxed">As a user, you agree to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Provide accurate, complete, and current information</li>
                <li>Maintain the confidentiality of your account credentials</li>
                <li>Notify NCAA immediately of any unauthorized access</li>
                <li>Use the service only for lawful purposes</li>
                <li>Comply with all NCAA regulations and civil aviation laws</li>
                <li>Submit only genuine documents and certifications</li>
              </ul>
            </section>

            {/* Prohibited Activities */}
            <section className="mb-8">
              <div className="flex items-center space-x-2 mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">6. Prohibited Activities</h2>
              </div>
              <p className="text-gray-700 mb-3 leading-relaxed">You must not:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Submit false or fraudulent information</li>
                <li>Impersonate another person or entity</li>
                <li>Attempt to circumvent security measures</li>
                <li>Use the service for commercial purposes without authorization</li>
                <li>Interfere with the proper functioning of the service</li>
                <li>Share your account with unauthorized individuals</li>
              </ul>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                NCAA shall not be liable for any indirect, incidental, special, consequential, or
                punitive damages arising from your use of the DCEV Portal. Our liability is limited
                to the extent permitted by Nigerian law.
              </p>
            </section>

            {/* Service Availability */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Service Availability</h2>
              <p className="text-gray-700 leading-relaxed">
                While we strive to maintain continuous service availability, NCAA does not guarantee
                uninterrupted access. We reserve the right to suspend or modify the service for
                maintenance, security, or regulatory compliance purposes.
              </p>
            </section>

            {/* Account Termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Account Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                NCAA reserves the right to suspend or terminate accounts that violate these Terms or
                engage in activities that compromise the integrity of the aviation licensing
                process. You may also close your account at any time by contacting our support team.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms are governed by the laws of the Federal Republic of Nigeria. Any
                disputes arising from these Terms shall be subject to the exclusive jurisdiction of
                Nigerian courts.
              </p>
            </section>

            {/* Updates */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Updates to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                NCAA may update these Terms from time to time. We will notify you of significant
                changes through the portal or via email. Continued use of the service after such
                changes constitutes acceptance of the new Terms.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
              <div className="bg-gray-50 p-6 rounded-lg border">
                <p className="text-gray-700 mb-3 font-semibold">
                  Nigeria Civil Aviation Authority (NCAA)
                </p>
                <div className="text-gray-600 space-y-1">
                  <p>
                    <strong>Email:</strong> support@dcev.ncaa.gov.ng
                  </p>
                  <p>
                    <strong>Phone:</strong> +234-9-461-5000
                  </p>
                  <p>
                    <strong>Address:</strong> Nnamdi Azikiwe International Airport, Abuja, Nigeria
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex justify-center space-x-4">
          <Button variant="outline" onClick={() => window.print()}>
            Print Terms
          </Button>
          <Link to="/register">
            <Button>Start Registration</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
